import { useState, useEffect, useCallback, useRef } from 'react'
import { ENABLE_DARK_MODE } from '../const/EndPoints'

export type ThemeMode = 'light' | 'auto' | 'dark'
export type EffectiveTheme = 'light' | 'dark'

const MODE_STORAGE_KEY = 'tw-theme-mode'
const LEGACY_STORAGE_KEY = 'tw-theme'
const FIRST_DARK_KEY = 'tw-first-dark-seen'

const LATE_NIGHT_START_HOUR = 22
const LATE_NIGHT_END_HOUR = 6

const isLateNightHour = (hour: number) =>
  hour >= LATE_NIGHT_START_HOUR || hour < LATE_NIGHT_END_HOUR

const getSystemPreference = (): EffectiveTheme => {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

const readInitialMode = (): ThemeMode => {
  if (!ENABLE_DARK_MODE) return 'light'
  if (typeof window === 'undefined') return 'auto'
  const stored = localStorage.getItem(MODE_STORAGE_KEY)
  if (stored === 'light' || stored === 'auto' || stored === 'dark') return stored
  // Migrate legacy explicit choices; default new users to Auto
  const legacy = localStorage.getItem(LEGACY_STORAGE_KEY)
  if (legacy === 'dark') return 'dark'
  if (legacy === 'light') return 'light'
  return 'auto'
}

const triggerGradientSweep = () => {
  const reducedMotion =
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
  const overlay = document.createElement('div')
  overlay.className = 'tw-theme-sweep'
  overlay.setAttribute('aria-hidden', 'true')
  document.body.appendChild(overlay)
  requestAnimationFrame(() => {
    overlay.classList.add(
      reducedMotion ? 'tw-theme-sweep--fade' : 'tw-theme-sweep--active'
    )
  })
  window.setTimeout(() => overlay.remove(), reducedMotion ? 200 : 600)
}

export function useTheme() {
  const [mode, setModeState] = useState<ThemeMode>(readInitialMode)
  const [systemTheme, setSystemTheme] = useState<EffectiveTheme>(getSystemPreference)
  const [now, setNow] = useState<Date>(() => new Date())

  const effectiveTheme: EffectiveTheme =
    mode === 'auto' ? systemTheme : (mode as EffectiveTheme)

  // Late-night dim only in Auto mode — explicit Light/Dark are user choices we don't override
  const isLateNight =
    ENABLE_DARK_MODE &&
    mode === 'auto' &&
    effectiveTheme === 'dark' &&
    isLateNightHour(now.getHours())

  // Listen to OS-level colour scheme changes so Auto follows live
  useEffect(() => {
    if (!ENABLE_DARK_MODE || typeof window === 'undefined' || !window.matchMedia) return
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) =>
      setSystemTheme(e.matches ? 'dark' : 'light')
    if (mql.addEventListener) {
      mql.addEventListener('change', handler)
      return () => mql.removeEventListener('change', handler)
    }
    // Safari < 14 fallback
    mql.addListener(handler)
    return () => mql.removeListener(handler)
  }, [])

  // Tick to the next hour boundary so late-night transitions fire without polling
  useEffect(() => {
    if (!ENABLE_DARK_MODE) return
    const next = new Date()
    next.setMinutes(0, 0, 0)
    next.setHours(next.getHours() + 1)
    const ms = Math.max(1000, next.getTime() - Date.now())
    const t = window.setTimeout(() => setNow(new Date()), ms)
    return () => window.clearTimeout(t)
  }, [now])

  // Apply data attributes to <html>
  useEffect(() => {
    const root = document.documentElement
    if (!ENABLE_DARK_MODE) {
      root.removeAttribute('data-theme')
      root.removeAttribute('data-time')
      return
    }
    root.setAttribute('data-theme', effectiveTheme)
    if (isLateNight) {
      root.setAttribute('data-time', 'late')
    } else {
      root.removeAttribute('data-time')
    }
  }, [effectiveTheme, isLateNight])

  // First-toggle gradient sweep — fires only the first time the user lands on dark
  const prevEffective = useRef<EffectiveTheme | null>(null)
  useEffect(() => {
    if (!ENABLE_DARK_MODE) {
      prevEffective.current = effectiveTheme
      return
    }
    const wasDark = prevEffective.current === 'dark'
    const becomingDark = !wasDark && effectiveTheme === 'dark'
    // Skip on initial mount when the hook seeds straight to dark — only animate on a real transition
    const isInitial = prevEffective.current === null
    prevEffective.current = effectiveTheme
    if (isInitial || !becomingDark) return
    const seen = localStorage.getItem(FIRST_DARK_KEY)
    if (seen) return
    triggerGradientSweep()
    localStorage.setItem(FIRST_DARK_KEY, '1')
  }, [effectiveTheme])

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next)
    if (typeof window === 'undefined') return
    localStorage.setItem(MODE_STORAGE_KEY, next)
    if (next === 'light' || next === 'dark') {
      localStorage.setItem(LEGACY_STORAGE_KEY, next)
    } else {
      localStorage.removeItem(LEGACY_STORAGE_KEY)
    }
  }, [])

  const toggleTheme = useCallback(() => {
    setMode(effectiveTheme === 'light' ? 'dark' : 'light')
  }, [effectiveTheme, setMode])

  return {
    mode,
    effectiveTheme,
    isLateNight,
    setMode,
    toggleTheme,
    isDark: effectiveTheme === 'dark',
  }
}
